import { useState } from "react";

import { OndoCode } from "../lib/OndoCode.tsx";
import type { CodeFacts } from "@ondo/shared";
import { WORK_CODE_COPY } from "./work.constants.ts";

/**
 * The work's ONDO code, and the only place it is shown.
 *
 * There is no lookup anywhere in this product that turns a code into a work —
 * `ondo.request_representation()` refuses every code it was not given, and one
 * opaque refusal covers a code that does not exist and a code that does. So the
 * only route from a work to an agent is a collector reading this panel and
 * passing it on, which is why the panel explains what it is for rather than
 * printing an identifier with no sentence beside it.
 *
 * It carries no year and no artist initials. That is what makes the second
 * field on the agent's form a second fact rather than a restatement of this
 * one — see 20260831090000's decision 1.
 *
 * THE MARK ABOVE THE STRING, and the two are the same code said twice. The bars
 * encode the eight characters printed beneath them, so a reader comparing the
 * two is comparing a thing with itself — which is the whole reason the mark is
 * an encoding rather than a texture. `work.constants.ts` used to carry a
 * `fingerprint` panel saying "the colour fingerprint is not generated yet" and
 * refusing a stand-in, because "a decorative stand-in would look like something
 * the registry had measured". That refusal is answered rather than overridden:
 * everything this mark shows came out of the code and the record.
 */
export function WorkCode({ code, facts }: { code: string; facts: CodeFacts }) {
  const [copied, setCopied] = useState(false);

  return (
    <section className="work__code">
      <h3 className="work__code-title">{WORK_CODE_COPY.title}</h3>

      {/* Renders nothing at all if the code cannot be drawn — a future minting
          scheme, a truncated value. The string below is then the whole panel,
          which is what it was before the mark existed. */}
      <OndoCode code={code} facts={facts} size="record" />

      <p className="work__code-value">{code}</p>

      {/* Best effort. `navigator.clipboard` is unavailable over plain HTTP and
          can be refused outright, and the code is already on screen and
          selectable — so a failure leaves the button saying what it says and
          costs the reader nothing. */}
      <button
        type="button"
        className="work__code-copy"
        onClick={() => {
          void navigator.clipboard
            ?.writeText(code)
            .then(() => setCopied(true))
            .catch(() => undefined);
        }}
      >
        {copied ? WORK_CODE_COPY.copied : WORK_CODE_COPY.copy}
      </button>

      <p className="work__code-detail">{WORK_CODE_COPY.detail}</p>
      <p className="work__code-detail work__code-detail--mark">{WORK_CODE_COPY.mark}</p>
    </section>
  );
}
